import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import toast from 'react-hot-toast';
import AdminLayout from '../components/AdminLayout';
import DebugImage from '../components/DebugImage';
import {
  PhotoIcon,
  PlusIcon,
  TrashIcon,
  PencilIcon,
  MagnifyingGlassIcon,
  FolderIcon,
  FolderPlusIcon,
  DocumentIcon,
  XMarkIcon,
  EyeIcon,
  VideoCameraIcon,
  ChartBarIcon,
  CloudIcon,
  CheckCircleIcon,
  Cog6ToothIcon
} from '@heroicons/react/24/outline';
import mediaApi from '../api/mediaApi';

const MediaLibrary = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedFileType, setSelectedFileType] = useState('all');
  const [selectedFolder, setSelectedFolder] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [previewFile, setPreviewFile] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editFile, setEditFile] = useState(null);
  const [showFolderModal, setShowFolderModal] = useState(false);
  const [showStatsModal, setShowStatsModal] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [viewMode, setViewMode] = useState('grid'); // grid or list

  const queryClient = useQueryClient();

  // Fetch media files
  const { data: mediaData, isLoading } = useQuery({
    queryKey: ['media', currentPage, selectedCategory, selectedFileType, selectedFolder, searchQuery],
    queryFn: () => mediaApi.getAllMedia({
      page: currentPage,
      limit: 20,
      category: selectedCategory,
      fileType: selectedFileType,
      folderId: selectedFolder?.id,
      search: searchQuery
    })
  });

  // Fetch categories
  const { data: categoriesData } = useQuery({
    queryKey: ['media-categories'],
    queryFn: () => mediaApi.getCategories()
  });

  // Fetch folders
  const { data: foldersData } = useQuery({
    queryKey: ['media-folders'],
    queryFn: () => mediaApi.getAllFolders()
  });

  // Fetch statistics
  const { data: statsData } = useQuery({
    queryKey: ['media-stats'],
    queryFn: () => mediaApi.getStats()
  });

  // Fetch CDN status
  const { data: cdnStatus } = useQuery({
    queryKey: ['cdn-status'],
    queryFn: () => mediaApi.getCdnStatus()
  });

  // Upload mutation
  const uploadMutation = useMutation({
    mutationFn: ({ files, options, onProgress }) => mediaApi.uploadFiles(files, options, onProgress),
    onSuccess: () => {
      toast.success('Files uploaded successfully!');
      queryClient.invalidateQueries(['media']);
      queryClient.invalidateQueries(['media-stats']);
      queryClient.invalidateQueries(['cdn-status']);
      setShowUploadModal(false);
    },
    onError: (error) => {
      toast.error(error.response?.data?.error || 'Upload failed');
    }
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: (id) => mediaApi.deleteMedia(id),
    onSuccess: () => {
      toast.success('File deleted successfully!');
      queryClient.invalidateQueries(['media']);
      queryClient.invalidateQueries(['media-stats']);
      setSelectedFiles([]);
    },
    onError: (error) => {
      toast.error(error.response?.data?.error || 'Delete failed');
    }
  });

  // Bulk delete mutation
  const bulkDeleteMutation = useMutation({
    mutationFn: (ids) => mediaApi.bulkDelete(ids),
    onSuccess: () => {
      toast.success('Files deleted successfully!');
      queryClient.invalidateQueries(['media']);
      queryClient.invalidateQueries(['media-stats']);
      setSelectedFiles([]);
    },
    onError: (error) => {
      toast.error(error.response?.data?.error || 'Bulk delete failed');
    }
  });

  // Folder mutations
  const createFolderMutation = useMutation({
    mutationFn: (data) => mediaApi.createFolder(data),
    onSuccess: () => {
      toast.success('Folder created successfully!');
      queryClient.invalidateQueries(['media-folders']);
      setShowFolderModal(false);
    },
    onError: (error) => {
      toast.error(error.response?.data?.error || 'Failed to create folder');
    }
  });

  const deleteFolderMutation = useMutation({
    mutationFn: (id) => mediaApi.deleteFolder(id),
    onSuccess: () => {
      toast.success('Folder deleted successfully!');
      queryClient.invalidateQueries(['media-folders']);
      setSelectedFolder(null);
    },
    onError: (error) => {
      toast.error(error.response?.data?.error || 'Failed to delete folder');
    }
  });

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: ({ id, data }) => mediaApi.updateMedia(id, data),
    onSuccess: () => {
      toast.success('File updated successfully!');
      queryClient.invalidateQueries(['media']);
      setShowEditModal(false);
      setEditFile(null);
    },
    onError: (error) => {
      toast.error(error.response?.data?.error || 'Update failed');
    }
  });

  const handleFileUpload = (files, options, onProgress) => {
    uploadMutation.mutate({ files, options, onProgress });
  };

  const handleDelete = (fileId) => {
    if (window.confirm('Are you sure you want to delete this file?')) {
      deleteMutation.mutate(fileId);
    }
  };

  const handleBulkDelete = () => {
    if (selectedFiles.length === 0) return;
    
    if (window.confirm(`Are you sure you want to delete ${selectedFiles.length} file(s)?`)) {
      bulkDeleteMutation.mutate(selectedFiles);
    }
  };

  const handleEdit = (file) => {
    setEditFile(file);
    setShowEditModal(true);
  };

  const handlePreview = (file) => {
    setPreviewFile(file);
    setShowPreviewModal(true);
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFileUpload(Array.from(e.dataTransfer.files));
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getFileIcon = (file) => {
    if (file.fileType === 'image' || file.mimeType?.startsWith('image/')) {
      return <PhotoIcon className="h-5 w-5" />;
    }
    if (file.fileType === 'video' || file.mimeType?.startsWith('video/')) {
      return <VideoCameraIcon className="h-5 w-5" />;
    }
    return <DocumentIcon className="h-5 w-5" />;
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
      {/* Header with Stats */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Media Library</h1>
          <div className="mt-2 flex items-center gap-4 text-sm">
            <span className="text-gray-700">
              {statsData?.totalFiles || 0} files • {statsData?.totalSize ? formatFileSize(statsData.totalSize) : '0 Bytes'}
            </span>
            {cdnStatus?.cdnConfigured && (
              <span className="inline-flex items-center text-green-700">
                <CloudIcon className="h-4 w-4 mr-1" />
                CDN Active
              </span>
            )}
          </div>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => setShowStatsModal(true)}
            className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
          >
            <ChartBarIcon className="h-4 w-4 mr-2" />
            Stats
          </button>
          <button
            onClick={() => setShowFolderModal(true)}
            className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
          >
            <FolderPlusIcon className="h-4 w-4 mr-2" />
            New Folder
          </button>
          <button
            onClick={() => setShowUploadModal(true)}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700"
          >
            <PlusIcon className="h-4 w-4 mr-2" />
            Upload Files
          </button>
        </div>
      </div>

      {/* Folders Navigation */}
      {foldersData?.folders && foldersData.folders.length > 0 && (
        <div className="flex items-center gap-2 overflow-x-auto pb-2">
          <button
            onClick={() => setSelectedFolder(null)}
            className={`px-3 py-1.5 text-sm rounded-md flex items-center gap-1 ${
              !selectedFolder ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            <FolderIcon className="h-4 w-4" />
            All Files
          </button>
          {foldersData.folders.map(folder => (
            <button
              key={folder.id}
              onClick={() => setSelectedFolder(folder)}
              className={`px-3 py-1.5 text-sm rounded-md flex items-center gap-1 ${
                selectedFolder?.id === folder.id ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
              style={{ borderLeft: `3px solid ${folder.color || '#3B82F6'}` }}
            >
              <FolderIcon className="h-4 w-4" />
              {folder.name}
              {folder.mediaCount > 0 && (
                <span className="ml-1 text-xs opacity-75">({folder.mediaCount})</span>
              )}
            </button>
          ))}
        </div>
      )}

      {/* Filters and Search */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="md:col-span-2">
          <div className="relative">
            <MagnifyingGlassIcon className="h-5 w-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search files..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
            />
          </div>
        </div>
        <div>
          <select
            value={selectedFileType}
            onChange={(e) => setSelectedFileType(e.target.value)}
            className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
          >
            <option value="all">All Types</option>
            <option value="image">Images</option>
            <option value="video">Videos</option>
            <option value="document">Documents</option>
            <option value="other">Other</option>
          </select>
        </div>
        <div>
          <select
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
            className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
          >
            <option value="all">All Categories</option>
            {categoriesData?.categories?.map(category => (
              <option key={category} value={category}>
                {category.charAt(0).toUpperCase() + category.slice(1)}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Bulk Actions */}
      {selectedFiles.length > 0 && (
        <div className="bg-blue-50 p-4 rounded-lg flex items-center justify-between">
          <span className="text-sm text-blue-800">
            {selectedFiles.length} file(s) selected
          </span>
          <button
            onClick={handleBulkDelete}
            className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded text-red-700 bg-red-100 hover:bg-red-200"
          >
            <TrashIcon className="h-4 w-4 mr-1" />
            Delete Selected
          </button>
        </div>
      )}

      {/* Media Grid */}
      {isLoading ? (
        <div className="text-center py-12">
          <div className="inline-flex items-center px-4 py-2 font-semibold leading-6 text-sm shadow rounded-md text-white bg-blue-500">
            <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            Loading...
          </div>
        </div>
      ) : (
        <div 
          className={`grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-4 ${
            dragActive ? 'bg-blue-50 border-2 border-dashed border-blue-300 rounded-lg p-4' : ''
          }`}
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
        >
          {mediaData?.files?.map((file) => (
            <div
              key={file.id}
              className="relative group bg-white border border-gray-200 rounded-lg overflow-hidden shadow-sm hover:shadow-md transition-shadow"
            >
              <input
                type="checkbox"
                checked={selectedFiles.includes(file.id)}
                onChange={(e) => {
                  if (e.target.checked) {
                    setSelectedFiles([...selectedFiles, file.id]);
                  } else {
                    setSelectedFiles(selectedFiles.filter(id => id !== file.id));
                  }
                }}
                className="absolute top-2 left-2 z-10 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              
              <div className="aspect-square bg-gray-100 flex items-center justify-center overflow-hidden relative">
                {file.fileType === 'image' || file.mimeType?.startsWith('image/') ? (
                  <DebugImage
                    src={file.thumbnailUrl ? mediaApi.getFileUrl(file) : mediaApi.getFileUrl(file.filename, true)}
                    alt={file.alt || file.originalName}
                    className="w-full h-full object-cover cursor-pointer"
                    onClick={() => handlePreview(file)}
                  />
                ) : file.fileType === 'video' && file.thumbnailUrl ? (
                  <div className="relative w-full h-full cursor-pointer" onClick={() => handlePreview(file)}>
                    <img
                      src={file.thumbnailUrl}
                      alt={file.originalName}
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="bg-black bg-opacity-50 rounded-full p-3">
                        <VideoCameraIcon className="h-8 w-8 text-white" />
                      </div>
                    </div>
                    {file.duration && (
                      <div className="absolute bottom-2 right-2 bg-black bg-opacity-75 text-white text-xs px-2 py-1 rounded">
                        {Math.floor(file.duration / 60)}:{String(Math.floor(file.duration % 60)).padStart(2, '0')}
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="text-gray-400">
                    {getFileIcon(file)}
                  </div>
                )}
                {file.isOptimized && (
                  <div className="absolute top-1 right-1 bg-green-500 text-white text-xs px-1.5 py-0.5 rounded">
                    <CheckCircleIcon className="h-3 w-3" />
                  </div>
                )}
                {file.cdnUrl && (
                  <div className="absolute bottom-1 left-1 bg-blue-500 text-white text-xs px-1.5 py-0.5 rounded flex items-center gap-0.5">
                    <CloudIcon className="h-3 w-3" />
                  </div>
                )}
              </div>
              
              <div className="p-3">
                <h3 className="text-sm font-medium text-gray-900 truncate" title={file.originalName}>
                  {file.originalName}
                </h3>
                <div className="flex items-center justify-between mt-1">
                  <p className="text-xs text-gray-500">
                    {formatFileSize(file.size)}
                  </p>
                  {file.width && file.height && (
                    <p className="text-xs text-gray-500">
                      {file.width}×{file.height}
                    </p>
                  )}
                </div>
                <div className="flex items-center gap-1 mt-1">
                  {file.folder && (
                    <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800">
                      <FolderIcon className="h-3 w-3 mr-0.5" />
                      {file.folder.name}
                    </span>
                  )}
                  {file.category && (
                    <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                      {file.category}
                    </span>
                  )}
                </div>
              </div>
              
              <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-10 transition-opacity">
                <div className="absolute top-2 right-2 flex space-x-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  <button
                    onClick={() => handlePreview(file)}
                    className="p-1 bg-white rounded-full shadow-sm hover:bg-gray-50"
                  >
                    <EyeIcon className="h-4 w-4 text-gray-600" />
                  </button>
                  <button
                    onClick={() => handleEdit(file)}
                    className="p-1 bg-white rounded-full shadow-sm hover:bg-gray-50"
                  >
                    <PencilIcon className="h-4 w-4 text-gray-600" />
                  </button>
                  <button
                    onClick={() => handleDelete(file.id)}
                    className="p-1 bg-white rounded-full shadow-sm hover:bg-gray-50"
                  >
                    <TrashIcon className="h-4 w-4 text-red-600" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Pagination */}
      {mediaData?.pagination && mediaData.pagination.totalPages > 1 && (
        <div className="flex items-center justify-between">
          <div className="text-sm text-gray-700">
            Showing page {mediaData.pagination.page} of {mediaData.pagination.totalPages}
          </div>
          <div className="flex space-x-2">
            <button
              onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
              disabled={currentPage === 1}
              className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Previous
            </button>
            <button
              onClick={() => setCurrentPage(Math.min(mediaData.pagination.totalPages, currentPage + 1))}
              disabled={currentPage === mediaData.pagination.totalPages}
              className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
            </button>
          </div>
        </div>
      )}

      {/* Upload Modal */}
      {showUploadModal && (
        <UploadModal
          onClose={() => setShowUploadModal(false)}
          onUpload={handleFileUpload}
          isUploading={uploadMutation.isPending}
        />
      )}

      {/* Preview Modal */}
      {showPreviewModal && previewFile && (
        <PreviewModal
          file={previewFile}
          onClose={() => setShowPreviewModal(false)}
        />
      )}

      {/* Edit Modal */}
      {showEditModal && editFile && (
        <EditModal
          file={editFile}
          onClose={() => setShowEditModal(false)}
          onSave={(data) => updateMutation.mutate({ id: editFile.id, data })}
          isSaving={updateMutation.isPending}
        />
      )}

      {/* Folder Modal */}
      {showFolderModal && (
        <FolderModal
          onClose={() => setShowFolderModal(false)}
          onCreate={(data) => createFolderMutation.mutate(data)}
          isCreating={createFolderMutation.isPending}
        />
      )}

      {/* Stats Modal */}
      {showStatsModal && (
        <StatsModal
          stats={statsData}
          cdnStatus={cdnStatus}
          onClose={() => setShowStatsModal(false)}
        />
      )}
      </div>
    </AdminLayout>
  );
};

// Upload Modal Component
const UploadModal = ({ onClose, onUpload, isUploading }) => {
  const [files, setFiles] = useState([]);
  const [category, setCategory] = useState('general');
  const [compressionQuality, setCompressionQuality] = useState('medium');
  const [optimize, setOptimize] = useState(true);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [dragActive, setDragActive] = useState(false);

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      setFiles(Array.from(e.dataTransfer.files));
    }
  };

  const handleFileSelect = (e) => {
    if (e.target.files && e.target.files[0]) {
      setFiles(Array.from(e.target.files));
    }
  };

  const handleUpload = () => {
    if (files.length > 0) {
      const options = {
        category,
        compressionQuality,
        optimize
      };
      onUpload(files, options, (progress) => setUploadProgress(progress));
    }
  };

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-medium text-gray-900">Upload Files</h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Category
              </label>
              <input
                type="text"
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                placeholder="general"
                className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Compression
              </label>
              <select
                value={compressionQuality}
                onChange={(e) => setCompressionQuality(e.target.value)}
                className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
              >
                <option value="high">High (90%)</option>
                <option value="medium">Medium (75%)</option>
                <option value="low">Low (60%)</option>
              </select>
            </div>
          </div>

          <div className="flex items-center">
            <input
              type="checkbox"
              id="optimize"
              checked={optimize}
              onChange={(e) => setOptimize(e.target.checked)}
              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
            />
            <label htmlFor="optimize" className="ml-2 block text-sm text-gray-900">
              Optimize images automatically
            </label>
          </div>

          <div
            className={`border-2 border-dashed rounded-lg p-6 text-center ${
              dragActive ? 'border-blue-400 bg-blue-50' : 'border-gray-300'
            }`}
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
          >
            <PhotoIcon className="mx-auto h-12 w-12 text-gray-400" />
            <div className="mt-4">
              <label htmlFor="file-upload" className="cursor-pointer">
                <span className="mt-2 block text-sm font-medium text-gray-900">
                  Drop files here or click to browse
                </span>
                <input
                  id="file-upload"
                  name="file-upload"
                  type="file"
                  multiple
                  accept="image/*,video/*"
                  className="sr-only"
                  onChange={handleFileSelect}
                />
              </label>
              <p className="mt-1 text-xs text-gray-500">
                Images, Videos up to 100MB (20 files max)
              </p>
            </div>
          </div>

          {files.length > 0 && (
            <div className="space-y-2">
              <h4 className="text-sm font-medium text-gray-900">Selected Files ({files.length}):</h4>
              <div className="max-h-32 overflow-y-auto space-y-1">
                {files.map((file, index) => (
                  <div key={index} className="flex items-center justify-between text-sm text-gray-600 bg-gray-50 px-2 py-1 rounded">
                    <span className="truncate flex-1">{file.name}</span>
                    <span className="ml-2">{(file.size / 1024 / 1024).toFixed(2)} MB</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {isUploading && uploadProgress > 0 && (
            <div className="space-y-2">
              <div className="flex justify-between text-sm text-gray-600">
                <span>Uploading...</span>
                <span>{uploadProgress}%</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                  style={{ width: `${uploadProgress}%` }}
                />
              </div>
            </div>
          )}

          <div className="flex justify-end space-x-3">
            <button
              onClick={onClose}
              disabled={isUploading}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              onClick={handleUpload}
              disabled={files.length === 0 || isUploading}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isUploading ? 'Uploading...' : `Upload ${files.length} file(s)`}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Preview Modal Component
const PreviewModal = ({ file, onClose }) => {
  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-10 mx-auto p-5 border max-w-4xl shadow-lg rounded-md bg-white">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-medium text-gray-900">{file.originalName}</h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        <div className="text-center">
          {file.fileType === 'image' || file.mimeType?.startsWith('image/') ? (
            <img
              src={mediaApi.getFileUrl(file)}
              alt={file.alt || file.originalName}
              className="max-w-full max-h-96 mx-auto rounded-lg"
            />
          ) : file.fileType === 'video' || file.mimeType?.startsWith('video/') ? (
            <video
              src={file.videoUrl || mediaApi.getFileUrl(file)}
              controls
              className="max-w-full max-h-96 mx-auto rounded-lg"
            >
              Your browser does not support the video tag.
            </video>
          ) : (
            <div className="text-gray-400 py-12">
              <DocumentIcon className="h-16 w-16 mx-auto" />
              <p className="mt-2">File preview not available</p>
            </div>
          )}
        </div>

        <div className="mt-4 grid grid-cols-2 gap-4 text-sm">
          <div>
            <span className="font-medium">File size:</span> {(file.size / 1024 / 1024).toFixed(2)} MB
          </div>
          <div>
            <span className="font-medium">Type:</span> {file.fileType || file.mimeType}
          </div>
          {file.width && file.height && (
            <div>
              <span className="font-medium">Dimensions:</span> {file.width}×{file.height}
            </div>
          )}
          {file.duration && (
            <div>
              <span className="font-medium">Duration:</span> {Math.floor(file.duration / 60)}:{String(Math.floor(file.duration % 60)).padStart(2, '0')}
            </div>
          )}
          <div>
            <span className="font-medium">Category:</span> {file.category || 'general'}
          </div>
          <div>
            <span className="font-medium">Uploaded:</span> {new Date(file.createdAt).toLocaleDateString()}
          </div>
          {file.isCompressed && (
            <div>
              <span className="font-medium">Compressed:</span> Yes ({file.compressionQuality}%)
            </div>
          )}
          {file.cdnUrl && (
            <div>
              <span className="font-medium">CDN:</span> <span className="text-green-600">Enabled</span>
            </div>
          )}
        </div>

        <div className="mt-4 p-3 bg-gray-50 rounded-md">
          <label className="block text-sm font-medium text-gray-700 mb-1">File URL:</label>
          <input
            type="text"
            value={file.cdnUrl || mediaApi.getFileUrl(file)}
            readOnly
            className="block w-full text-sm text-gray-900 bg-white border border-gray-300 rounded-md px-3 py-2"
            onClick={(e) => e.target.select()}
          />
        </div>
      </div>
    </div>
  );
};

// Edit Modal Component
const EditModal = ({ file, onClose, onSave, isSaving }) => {
  const [formData, setFormData] = useState({
    alt: file.alt || '',
    title: file.title || '',
    category: file.category || 'general'
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-medium text-gray-900">Edit File</h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Alt Text
            </label>
            <input
              type="text"
              value={formData.alt}
              onChange={(e) => setFormData({ ...formData, alt: e.target.value })}
              className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Title
            </label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Category
            </label>
            <input
              type="text"
              value={formData.category}
              onChange={(e) => setFormData({ ...formData, category: e.target.value })}
              className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
            />
          </div>

          <div className="flex justify-end space-x-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSaving}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSaving ? 'Saving...' : 'Save'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Folder Modal Component
const FolderModal = ({ onClose, onCreate, isCreating }) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    color: '#3B82F6'
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onCreate(formData);
  };

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-medium text-gray-900">Create New Folder</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Folder Name *
            </label>
            <input
              type="text"
              required
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
              placeholder="My Folder"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Description
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              rows={3}
              className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
              placeholder="Optional description"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Color
            </label>
            <div className="flex items-center gap-2">
              <input
                type="color"
                value={formData.color}
                onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                className="h-10 w-20 rounded border-gray-300"
              />
              <input
                type="text"
                value={formData.color}
                onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                className="flex-1 rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
              />
            </div>
          </div>

          <div className="flex justify-end space-x-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isCreating}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isCreating ? 'Creating...' : 'Create Folder'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Stats Modal Component
const StatsModal = ({ stats, cdnStatus, onClose }) => {
  const formatFileSize = (bytes) => {
    if (!bytes || bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-10 mx-auto p-5 border max-w-2xl shadow-lg rounded-md bg-white">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-medium text-gray-900">Media Library Statistics</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        <div className="space-y-6">
          {/* Overview */}
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-blue-50 p-4 rounded-lg">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Total Files</p>
                  <p className="text-2xl font-bold text-blue-600">{stats?.totalFiles || 0}</p>
                </div>
                <PhotoIcon className="h-10 w-10 text-blue-600" />
              </div>
            </div>
            <div className="bg-green-50 p-4 rounded-lg">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Total Size</p>
                  <p className="text-2xl font-bold text-green-600">{formatFileSize(stats?.totalSize)}</p>
                </div>
                <ChartBarIcon className="h-10 w-10 text-green-600" />
              </div>
            </div>
          </div>

          {/* File Types */}
          {stats?.fileTypeStats && (
            <div>
              <h4 className="text-sm font-medium text-gray-900 mb-3">By File Type</h4>
              <div className="space-y-2">
                {Object.entries(stats.fileTypeStats).map(([type, count]) => (
                  <div key={type} className="flex items-center justify-between">
                    <span className="text-sm text-gray-600 capitalize">{type}</span>
                    <span className="text-sm font-medium text-gray-900">{count} files</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* CDN Status */}
          {cdnStatus && (
            <div className="border-t pt-4">
              <h4 className="text-sm font-medium text-gray-900 mb-3">CDN Status</h4>
              {cdnStatus.cdnConfigured ? (
                <div className="space-y-3">
                  <div className="flex items-center text-green-600">
                    <CloudIcon className="h-5 w-5 mr-2" />
                    <span className="font-medium">CDN Active</span>
                    <span className="ml-2 text-xs bg-green-100 px-2 py-1 rounded">
                      {cdnStatus.cloudName}
                    </span>
                  </div>
                  {cdnStatus.stats && (
                    <div className="grid grid-cols-3 gap-4 text-sm">
                      <div>
                        <p className="text-gray-600">Total Media</p>
                        <p className="font-medium">{cdnStatus.stats.totalMedia}</p>
                      </div>
                      <div>
                        <p className="text-gray-600">On CDN</p>
                        <p className="font-medium text-green-600">{cdnStatus.stats.cdnMedia}</p>
                      </div>
                      <div>
                        <p className="text-gray-600">Local Only</p>
                        <p className="font-medium">{cdnStatus.stats.localMedia}</p>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="flex items-center text-gray-500">
                  <CloudIcon className="h-5 w-5 mr-2" />
                  <span>CDN not configured</span>
                </div>
              )}
            </div>
          )}

          {/* Recent Files */}
          {stats?.recentFiles && stats.recentFiles.length > 0 && (
            <div className="border-t pt-4">
              <h4 className="text-sm font-medium text-gray-900 mb-3">Recent Uploads</h4>
              <div className="space-y-2">
                {stats.recentFiles.map(file => (
                  <div key={file.id} className="flex items-center justify-between text-sm">
                    <span className="text-gray-600 truncate flex-1">{file.originalName}</span>
                    <span className="text-gray-500 ml-2">{formatFileSize(file.size)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="mt-6 flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default MediaLibrary;